import { arrowHead, DEFAULT_ARROW_STROKE_WIDTH, type ArrowHeadStyle } from './arrowHeadGeometry.ts';
// #427 - shared, framework-free geometry for canvas annotations, so the live vector layer
// (PaperVectorLayer) and the SVG string export (svgExport) draw the exact same mark. Reaction
// (scheme) arrows have their own module (reactionArrowGeometry.ts); this covers the mechanism
// (curved) arrow, decorative-shape star points, and the dash patterns. Everything is in the
// caller's pixel space; the caller strokes/fills and owns the colour.

export interface AnnotationPoint {
  readonly x: number;
  readonly y: number;
}

/** `stroke-dasharray` for each annotation line style (undefined = solid). */
export const ANNOTATION_DASH: Readonly<Record<'solid' | 'dashed' | 'dotted', string | undefined>> = {
  solid: undefined,
  dashed: '6 4',
  dotted: '1 4'
};

// The 10 points of a five-pointed star inscribed in the bounding box (outer points on the box,
// inner points at 0.4 of the way in). Matches PaperVectorLayer's decorative `star` shape.
export function starPoints(minX: number, minY: number, maxX: number, maxY: number): readonly AnnotationPoint[] {
  const cx = (minX + maxX) / 2;
  const cy = (minY + maxY) / 2;
  const outerX = (maxX - minX) / 2;
  const outerY = (maxY - minY) / 2;
  return Array.from({ length: 10 }, (_, index) => {
    const angle = -Math.PI / 2 + (index * Math.PI) / 5;
    const scale = index % 2 === 0 ? 1 : 0.4;
    return { x: cx + Math.cos(angle) * outerX * scale, y: cy + Math.sin(angle) * outerY * scale };
  });
}

export type CurvedArrowPrimitive =
  | { readonly t: 'path'; readonly d: string }
  | { readonly t: 'polyline'; readonly points: readonly AnnotationPoint[] }
  | {
      readonly t: 'polygon';
      readonly points: readonly AnnotationPoint[];
      readonly filled: boolean;
      readonly head?: ArrowHeadStyle;
    };

/**
 * #933: how far a source end anchored to an atom (or its lone pair) stands off the atom, in pixels -- it
 * sits beside the atom, on the curve's side, like the lone pair it stands for, and never on the label.
 */
export const ATOM_END_GAP = 12;

/**
 * #1228: how far an arrowhead tip anchored to a target atom stands off the atom, in pixels.
 * Narrowed from 12 px so the arrowhead tip reaches close to the target atom / label.
 */
export const ATOM_TARGET_GAP = 6;

/**
 * #933, #1228: the gap each end keeps -- {@link ATOM_END_GAP} for a source atom or lone pair,
 * {@link ATOM_TARGET_GAP} for a target atom or lone pair, and none otherwise.
 */
export function atomEndGaps(
  fromAnchor?: { readonly kind: string },
  toAnchor?: { readonly kind: string }
): { readonly from: number; readonly to: number } {
  const from =
    fromAnchor && (fromAnchor.kind === 'atom' || fromAnchor.kind === 'lone-pair') ? ATOM_END_GAP : 0;
  const to =
    toAnchor && (toAnchor.kind === 'atom' || toAnchor.kind === 'lone-pair') ? ATOM_TARGET_GAP : 0;
  return { from, to };
}

/** A point moved `distance` toward `toward` (at most a third of the way, so short arrows keep a body). */
function standOff(point: AnnotationPoint, toward: AnnotationPoint, distance: number): AnnotationPoint {
  const length = Math.hypot(toward.x - point.x, toward.y - point.y);
  if (distance <= 0 || length < 1e-9) return point;
  const k = Math.min(distance, length / 3) / length;
  return { x: point.x + (toward.x - point.x) * k, y: point.y + (toward.y - point.y) * k };
}

// Cubic controls and legacy quadratic bows share sharp, stroke-scaled heads.
export function curvedArrowPrimitives(
  anchoredFrom: AnnotationPoint,
  anchoredTo: AnnotationPoint,
  head: 'full' | 'half' | 'multi',
  bow: number,
  strokeWidth = DEFAULT_ARROW_STROKE_WIDTH,
  zoom = 1,
  controls?: readonly [AnnotationPoint, AnnotationPoint],
  endGaps: { readonly from: number; readonly to: number } = { from: 0, to: 0 }
): readonly CurvedArrowPrimitive[] {
  // #933: an end on an atom stands off it toward its own control point -- the curve's side.
  const from = standOff(anchoredFrom, controls?.[0] ?? anchoredTo, endGaps.from);
  const to = standOff(anchoredTo, controls?.[1] ?? anchoredFrom, endGaps.to);
  const midX = (from.x + to.x) / 2;
  const midY = (from.y + to.y) / 2;
  const length = Math.hypot(to.x - from.x, to.y - from.y) || 1;
  const perpX = -(to.y - from.y) / length;
  const perpY = (to.x - from.x) / length;
  const controlX = midX + perpX * bow;
  const controlY = midY + perpY * bow;
  const tangentOrigin = controls
    ? ([controls[1], controls[0], from].find((point) => Math.hypot(to.x - point.x, to.y - point.y) > 1e-9) ?? from)
    : { x: controlX, y: controlY };
  const tangentX = to.x - tangentOrigin.x;
  const tangentY = to.y - tangentOrigin.y;
  const arrowhead = arrowHead(
    to,
    { x: tangentX, y: tangentY },
    head === 'half' ? 'half' : 'swallowtail',
    strokeWidth,
    1,
    strokeWidth / zoom
  );
  const distance = Math.hypot(to.x - arrowhead.base.x, to.y - arrowhead.base.y);
  const lerp = (a: AnnotationPoint, b: AnnotationPoint, t: number): AnnotationPoint => ({
    x: a.x + (b.x - a.x) * t,
    y: a.y + (b.y - a.y) * t
  });
  const splitCubic = (t: number) => {
    const a = lerp(from, controls![0], t);
    const b = lerp(controls![0], controls![1], t);
    const c = lerp(controls![1], to, t);
    const d = lerp(a, b, t);
    return [a, d, lerp(d, lerp(b, c, t), t)] as const;
  };
  const at = (t: number) =>
    controls
      ? splitCubic(t)[2]
      : {
          x: (1 - t) ** 2 * from.x + 2 * (1 - t) * t * controlX + t * t * to.x,
          y: (1 - t) ** 2 * from.y + 2 * (1 - t) * t * controlY + t * t * to.y
        };
  let low = 0;
  let high = 1;
  for (let i = 0; i < 32; i++) {
    const mid = (low + high) / 2;
    const point = at(mid);
    if (Math.hypot(to.x - point.x, to.y - point.y) > distance) low = mid;
    else high = mid;
  }
  const t = (low + high) / 2;
  const end = at(t);
  // De Casteljau preserves the visible curve; the short connector ends inside the
  // head so no stroked shaft or round cap can protrude through its apex.
  const cubic = controls ? splitCubic(t) : undefined;
  return [
    {
      t: 'path',
      d: cubic
        ? `M ${from.x} ${from.y} C ${cubic[0].x} ${cubic[0].y} ${cubic[1].x} ${cubic[1].y} ${end.x} ${end.y} L ${arrowhead.base.x} ${arrowhead.base.y}`
        : `M ${from.x} ${from.y} Q ${from.x + (controlX - from.x) * t} ${from.y + (controlY - from.y) * t} ${end.x} ${end.y} L ${arrowhead.base.x} ${arrowhead.base.y}`
    },
    arrowhead.primitive
  ];
}

// #866 (#842 S5; owner, 2026-10-07) -- one fixed drop shadow, crisp so every export format draws it
// the same (the PDF exporter has no SVG filters): a copy of the object behind it, offset down-right.
export const SHADOW_OFFSET_PT = 2;
export const SHADOW_COLOR = '#000000';
export const SHADOW_OPACITY = 0.2;
